import { FiberManualRecord } from '@mui/icons-material';
import { List, ListItem, ListItemButton, ListItemText, Typography, styled, useTheme } from '@mui/material';
import { type RefObject } from 'react';
import { BackdropImage } from '#components/BackdropImage/index.tsx';
import { SectionType } from '#enums/mediaTypes.ts';
import { formatAirDate } from '#helpers/formatAirDate.ts';
import { formatRuntime } from '#helpers/formatRuntime.ts';
import { type EpisodeData } from './types.ts';

export type EpisodesProps = {
  episodes: EpisodeData[];
  onEpisodeClick: (season: number, episode: number) => void;
  seasonNumber: number;
  /**
   * Is the parent season selected
   */
  seasonSelected: boolean;
  selectedEpisodeNumber: number;
  selectedEpisodeRef: RefObject<HTMLLIElement | null>;
};

const Content = styled('div')();

export const Episodes = ({
  episodes,
  onEpisodeClick,
  seasonNumber,
  seasonSelected,
  selectedEpisodeNumber,
  selectedEpisodeRef,
}: EpisodesProps) => {
  const theme = useTheme();

  return (
    <List
      sx={{
        color: theme.mainTextPrimary,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: 0,
        width: '100%',
      }}
    >
      {episodes.map(({ airDate, episodeNumber, id, runtime, src, subTitle, title }) => (
        <ListItem
          disablePadding
          key={id}
          ref={seasonSelected && episodeNumber === selectedEpisodeNumber ? selectedEpisodeRef : undefined}
          sx={{ scrollMarginTop: '50px' }}
        >
          <ListItemButton
            disableRipple
            onClick={() => {
              onEpisodeClick(seasonNumber, episodeNumber);
            }}
            sx={{
              ':active': {
                backgroundColor: '#181818',
              },
              alignItems: 'flex-start',
              backgroundColor: seasonSelected && episodeNumber === selectedEpisodeNumber ? '#181818' : undefined,
              boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2)',
              padding: theme.spacing(2),
              width: '100%',
            }}
          >
            <BackdropImage
              id={id}
              src={src}
              sx={{
                [theme.breakpoints.up('sm')]: {
                  width: '230px',
                },
                flexShrink: 0,
                marginRight: theme.spacing(2),
                width: '150px',
              }}
              title={title}
              type={SectionType.Episode}
            />
            <Content>
              <ListItemText primary={title} />
              <Typography sx={{ color: theme.supportingTextPrimary, marginBottom: theme.spacing(0.5) }} variant="body2">
                {subTitle}
              </Typography>
              <div>
                <Typography sx={{ color: theme.supportingTextPrimary }} variant="body2">
                  {airDate ? <span>{formatAirDate(airDate, 'MMM d, yyyy')}</span> : undefined}
                  {airDate && runtime ? (
                    <FiberManualRecord
                      sx={{
                        height: '10px',
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(1),
                        width: '10px',
                      }}
                    />
                  ) : undefined}
                  {runtime ? <span>{formatRuntime(runtime)}</span> : undefined}
                </Typography>
              </div>
            </Content>
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};
